import type React from "react";
import { useEffect, useState } from "react";
import { Shield, Globe, LockKeyhole, CheckCircle2, Minus } from "lucide-react";
import { theme, card, inputStyle, btnPrimary, btnSecondary } from "../theme";
import { api } from "../api/client";

interface Provider {
  name: string;
  icon: React.ReactNode;
  status: "live" | "invite-flow" | "not-available";
  desc: string;
  details: string[];
}

const providers: Provider[] = [
  {
    name: "Microsoft Entra ID",
    icon: <Shield size={18} color="#0078d4" />,
    status: "live",
    desc: "Azure Active Directory / Entra ID",
    details: [
      "Client and consultant users sign in with their Microsoft 365 credentials.",
      "Invite a user from the Users page: they sign in immediately using their existing Microsoft account.",
      "Auditors can also sign in with Entra ID if their account was provisioned with an Entra SSO provider.",
      "Tenant mapping is handled automatically on first sign-in.",
    ],
  },
  {
    name: "Google Workspace",
    icon: <Globe size={18} color="#4285f4" />,
    status: "live",
    desc: "Google Workspace hosted domain",
    details: [
      "All roles (client, consultant, auditor) can sign in with Google.",
      "Invite a user from the Users page with their Google Workspace email address.",
      "On first sign-in their Google account is linked automatically; no extra configuration is needed.",
      "Personal Gmail addresses (@gmail.com) are not supported; Workspace accounts only.",
    ],
  },
  {
    name: "SAML 2.0",
    icon: <LockKeyhole size={18} color="var(--c-muted)" />,
    status: "live",
    desc: "Generic SAML 2.0 identity provider",
    details: [
      "SAML identity providers are configured per client organisation, below.",
      "Supports any SAML 2.0-compliant identity provider (Okta, Azure AD, PingFederate, etc.).",
      "Users invited from the Users page can sign in with SAML after configuration.",
      "Auditors can also authenticate via SAML if their account is provisioned with a SAML provider.",
    ],
  },
];

const STATUS_LABELS: Record<Provider["status"], { label: string; color: string; bg: string }> = {
  live:           { label: "Live",         color: theme.color.success,    bg: theme.color.successBg },
  "invite-flow":  { label: "Via invite",   color: theme.color.accentText, bg: theme.color.accentBg },
  "not-available":{ label: "Coming soon",  color: theme.color.faint,      bg: theme.color.panel },
};

// ── SAML configuration for one client ─────────────────────────────────────
// GET / PUT / DELETE /auth/saml/config?organizationId= (routes/saml.routes.ts,
// org:admin over the client and firm:admin, the same as this page's route). The
// IdP certificate is never sent back: left blank on an edit, the one on file stays.

interface SamlConfig {
  samlEnabled: boolean;
  samlIdpEntityId: string | null;
  samlIdpSsoUrl: string | null;
  samlSpEntityId: string;
  acsUrl: string;
  metadataUrl: string;
}

function SamlConfigCard() {
  const [clients, setClients] = useState<Array<{ id: string; name: string }>>([]);
  const [orgId, setOrgId] = useState("");
  const [config, setConfig] = useState<SamlConfig | null>(null);
  const [form, setForm] = useState({ idpEntityId: "", idpSsoUrl: "", idpCertificate: "", spEntityId: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState("");

  useEffect(() => {
    api.get<Array<{ id: string; name: string }>>("/consultant/clients")
      .then((list) => setClients(list))
      .catch(() => setError("Could not load your clients."));
  }, []);

  useEffect(() => {
    setConfig(null); setSaved(""); setError("");
    if (!orgId) return;
    api.get<SamlConfig>("/auth/saml/config", { organizationId: orgId })
      .then((c) => {
        setConfig(c);
        setForm({ idpEntityId: c.samlIdpEntityId ?? "", idpSsoUrl: c.samlIdpSsoUrl ?? "", idpCertificate: "", spEntityId: c.samlEnabled ? c.samlSpEntityId : "" });
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Could not load the SAML configuration."));
  }, [orgId]);

  async function save() {
    setSaving(true); setError(""); setSaved("");
    try {
      await api.put("/auth/saml/config", {
        idpEntityId: form.idpEntityId.trim(),
        idpSsoUrl: form.idpSsoUrl.trim(),
        ...(form.idpCertificate.trim() ? { idpCertificate: form.idpCertificate.trim() } : {}),
        ...(form.spEntityId.trim() ? { spEntityId: form.spEntityId.trim() } : {}),
      }, { organizationId: orgId });
      const c = await api.get<SamlConfig>("/auth/saml/config", { organizationId: orgId });
      setConfig(c);
      setForm((f) => ({ ...f, idpCertificate: "" }));
      setSaved("SAML sign-in is on for this client.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the SAML configuration.");
    } finally {
      setSaving(false);
    }
  }

  async function disable() {
    if (!confirm("Turn SAML sign-in off for this client and clear its identity provider settings? Members who sign in with SAML will need another sign-in method.")) return;
    setSaving(true); setError(""); setSaved("");
    try {
      await api.del(`/auth/saml/config`, { organizationId: orgId });
      setConfig((c) => (c ? { ...c, samlEnabled: false, samlIdpEntityId: null, samlIdpSsoUrl: null } : c));
      setForm({ idpEntityId: "", idpSsoUrl: "", idpCertificate: "", spEntityId: "" });
      setSaved("SAML sign-in is off for this client.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not turn SAML off.");
    } finally {
      setSaving(false);
    }
  }

  const field = (label: string, key: keyof typeof form, props: React.InputHTMLAttributes<HTMLInputElement> = {}) => (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: theme.color.muted }}>
      {label}
      <input {...props} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} style={{ ...inputStyle, width: "100%", boxSizing: "border-box" as const }} />
    </label>
  );
  const canSave = !!form.idpEntityId.trim() && /^https:\/\//i.test(form.idpSsoUrl.trim()) && (!!form.idpCertificate.trim() || !!config?.samlEnabled);

  return (
    <div style={{ ...card, display: "flex", flexDirection: "column", gap: 12, marginTop: 20 }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>Configure SAML for a client</div>
      <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6 }}>
        Choose the client, then enter the details from its identity provider. Give the provider the service provider entity ID,
        ACS URL and metadata link shown below.
      </div>
      <select value={orgId} onChange={(e) => setOrgId(e.target.value)} aria-label="Client organisation" style={{ ...inputStyle, maxWidth: 360 }}>
        <option value="">Choose a client…</option>
        {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {error && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
      {saved && <div style={{ fontSize: 12, color: theme.color.successText }}>{saved}</div>}
      {orgId && config && (
        <>
          <div style={{ fontSize: 12, color: theme.color.subtle }}>
            Status: <strong style={{ color: config.samlEnabled ? theme.color.successText : theme.color.muted }}>{config.samlEnabled ? "SAML sign-in on" : "Not configured"}</strong>
          </div>
          <div style={{ background: theme.color.bg, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "10px 14px", fontSize: 12, color: theme.color.subtle, display: "flex", flexDirection: "column", gap: 4, wordBreak: "break-all" }}>
            <div>Service provider entity ID: <code>{config.samlSpEntityId}</code></div>
            <div>ACS URL: <code>{config.acsUrl}</code></div>
            <div>Metadata: <code>{config.metadataUrl}</code></div>
          </div>
          {field("Identity provider entity ID", "idpEntityId", { placeholder: "https://sts.example.com/…" })}
          {field("Identity provider sign-in URL (HTTPS)", "idpSsoUrl", { type: "url", placeholder: "https://idp.example.com/sso/saml" })}
          <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 11, color: theme.color.muted }}>
            {config.samlEnabled ? "Signing certificate (leave blank to keep the one on file)" : "Signing certificate (PEM)"}
            <textarea value={form.idpCertificate} onChange={(e) => setForm({ ...form, idpCertificate: e.target.value })} rows={4}
              placeholder="-----BEGIN CERTIFICATE-----" style={{ ...inputStyle, width: "100%", boxSizing: "border-box" as const, fontFamily: "monospace", fontSize: 11, resize: "vertical" }} />
          </label>
          {field("Service provider entity ID override (optional)", "spEntityId")}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={save} disabled={saving || !canSave} style={{ ...btnPrimary, opacity: saving || !canSave ? 0.6 : 1 }}>
              {saving ? "Saving…" : config.samlEnabled ? "Save changes" : "Turn on SAML"}
            </button>
            {config.samlEnabled && <button onClick={disable} disabled={saving} style={{ ...btnSecondary, color: theme.color.dangerText }}>Turn SAML off</button>}
          </div>
        </>
      )}
    </div>
  );
}

// ── SSO page (/consultant/settings/sso) ───────────────────────────────────
// How users across every role sign in, and the per-client SAML configuration.
// Microsoft and Google need nothing configured: the provider follows the email address invited.

export function ConsultantSsoPage() {
  return (
    <div className="page-pad" style={{ fontFamily: theme.font }}>
      <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        SSO &amp; Identity Providers
      </h1>
      <p style={{ margin: "0 0 20px", fontSize: 13, color: theme.color.subtle }}>
        How users across all roles sign in to the platform.
      </p>

      <div style={{ ...card, display: "flex", flexDirection: "column", gap: 24 }}>
        <div
          style={{
            background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`,
            borderRadius: 3, padding: 14, fontSize: 12, color: theme.color.accentText, lineHeight: 1.6,
          }}
        >
          <strong>Invite-based access:</strong> there is no self-registration. Users are provisioned through the
          invite flow on the Users page. The SSO provider is determined by the email address you invite;
          no separate SSO configuration is required.
        </div>

        {providers.map((p) => {
          const badge = STATUS_LABELS[p.status];
          return (
            <div key={p.name} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <span style={{ fontSize: 18, flexShrink: 0, display: "flex" }}>{p.icon}</span>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: theme.color.heading }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: theme.color.muted }}>{p.desc}</div>
                  </div>
                </div>
                <span style={{
                  flexShrink: 0, fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 3,
                  color: badge.color, background: badge.bg, marginLeft: 12,
                }}>
                  {badge.label}
                </span>
              </div>

              <div
                style={{
                  background: theme.color.bg, border: `1px solid ${theme.color.border}`,
                  borderRadius: 3, padding: "14px 16px", display: "flex", flexDirection: "column", gap: 8,
                }}
              >
                {p.details.map((line, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    {p.status === "not-available"
                      ? <Minus size={13} style={{ flexShrink: 0, marginTop: 2 }} color={theme.color.faint} />
                      : <CheckCircle2 size={13} style={{ flexShrink: 0, marginTop: 2 }} color={theme.color.success} />
                    }
                    <span style={{ fontSize: 12, color: p.status === "not-available" ? theme.color.faint : theme.color.subtle, lineHeight: 1.6 }}>
                      {line}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <SamlConfigCard />
    </div>
  );
}
